import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Collapse, Pagination } from 'react-bootstrap';
import recallService from '../../../services/recalls.service';
import Loading from '../../common/Loading';
import ProcessQueueFilters from '../../process/ProcessQueueFilters';
import {
    RECALL_STATUS_BADGES,
    formatStatusLabel,
} from '../../process/processQueueUtils';

const SEARCH_FIELDS = [
    { value: 'lastName', label: 'Last Name' },
    { value: 'firstName', label: 'First Name' },
    { value: 'email', label: 'Email' },
    { value: 'cellNumber', label: 'Cell Number' },
    { value: 'clinicName', label: 'Clinic Name' },
];

const SORT_FIELDS = [
    { value: 'createdAt', label: 'Created At' },
    { value: 'modifiedAt', label: 'Modified At' },
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'status', label: 'Status' },
    { value: 'clinicName', label: 'Clinic Name' },
];

const STATUS_OPTIONS = ['PENDING', 'IN_PROCESS', 'SENT', 'FAILED', 'COMPLETED'];

function RecallPoolTable({ searchPanelOpen = false }) {
    const navigate = useNavigate();
    const [currentItems, setCurrentItems] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [searchField, setSearchField] = useState('lastName');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');

    const loadPools = () => {
        setIsLoading(true);
        setError(null);

        return recallService
            .getRecallPools(currentPage, itemsPerPage, {
                sortBy,
                sortOrder,
                searchField,
                searchTerm: appliedSearch,
                statusFilter,
            })
            .then((res) => {
                setCurrentItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                if ((res.data.content || []).length === 0 && (appliedSearch || statusFilter)) {
                    setError('No recall pools match your filters.');
                }
            })
            .catch((err) => {
                console.error(err);
                setError('Failed to load recall pools. Please try again.');
                setCurrentItems([]);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        let isActive = true;

        setIsLoading(true);
        setError(null);

        recallService
            .getRecallPools(currentPage, itemsPerPage, {
                sortBy,
                sortOrder,
                searchField,
                searchTerm: appliedSearch,
                statusFilter,
            })
            .then((res) => {
                if (!isActive) {
                    return;
                }
                setCurrentItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                if ((res.data.content || []).length === 0 && (appliedSearch || statusFilter)) {
                    setError('No recall pools match your filters.');
                }
            })
            .catch((err) => {
                if (!isActive) {
                    return;
                }
                console.error(err);
                setError('Failed to load recall pools. Please try again.');
                setCurrentItems([]);
            })
            .finally(() => {
                if (isActive) {
                    setIsLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [currentPage, itemsPerPage, appliedSearch, statusFilter, sortBy, sortOrder, searchField]);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        setAppliedSearch(searchTerm);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setStatusFilter('');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleView = (id) => {
        navigate(`edit/${id}`);
    };

    const handleDelete = async (item) => {
        const name = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'this entry';
        if (!window.confirm(`Delete ${name} from the recall pool?`)) {
            return;
        }

        try {
            await recallService.deleteRecallPool(item.id);
            await loadPools();
        } catch (err) {
            console.error(err);
            setError('Failed to delete recall pool entry. Please try again.');
        }
    };

    const StatusBadge = ({ status }) => {
        const badgeClass = RECALL_STATUS_BADGES[status] || 'text-bg-secondary';
        return <span className={`badge ${badgeClass}`}>{formatStatusLabel(status)}</span>;
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage + 1);
        let endPage = Math.min(totalPages - 1, currentPage + 3);

        if (currentPage < 2) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={currentPage === totalPages - 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = currentItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    const patientName = (item) =>
        `${item.firstName || ''} ${item.lastName || ''}`.trim() || '—';

    return (
        <>
            <Collapse in={searchPanelOpen}>
                <div>
                    <ProcessQueueFilters
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                        searchField={searchField}
                        onSearchFieldChange={setSearchField}
                        searchFields={SEARCH_FIELDS}
                        statusFilter={statusFilter}
                        onStatusFilterChange={(value) => {
                            setStatusFilter(value);
                            setCurrentPage(0);
                        }}
                        statusOptions={STATUS_OPTIONS}
                        sortBy={sortBy}
                        onSortByChange={(value) => {
                            setSortBy(value);
                            setCurrentPage(0);
                        }}
                        sortFields={SORT_FIELDS}
                        sortOrder={sortOrder}
                        onSortOrderChange={(value) => {
                            setSortOrder(value);
                            setCurrentPage(0);
                        }}
                        onSubmit={handleSearchSubmit}
                        onClear={handleClearFilters}
                        canClear={Boolean(appliedSearch || statusFilter || searchTerm)}
                        showSort
                    />
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : error && currentItems.length === 0 ? (
                <Alert variant="warning">{error}</Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                        <thead className="table-light">
                            <tr>
                                <th>
                                    <i className="bi bi-building"></i> Clinic
                                </th>
                                <th>
                                    <i className="bi bi-person"></i> Patient
                                </th>
                                <th>
                                    <i className="bi bi-envelope"></i> Contact
                                </th>
                                <th>
                                    <i className="bi bi-broadcast"></i> Medium
                                </th>
                                <th>
                                    <i className="bi bi-calendar"></i> Created
                                </th>
                                <th>
                                    <i className="bi bi-activity"></i> Status
                                </th>
                                <th style={{ width: '140px' }} className="text-center">
                                    <i className="bi bi-gear"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">
                                        No recall pool entries found.
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((item) => {
                                    const isPending = item.status === 'PENDING';
                                    const rowClassName = [
                                        isPending ? 'fw-bold' : '',
                                        expandedRowId === item.id ? 'table-active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ');

                                    return (
                                        <React.Fragment key={item.id}>
                                            <tr className={rowClassName || undefined}>
                                                <td>{item.clinicName || '—'}</td>
                                                <td>
                                                    <small>{patientName(item)}</small>
                                                    {item.preferredName && (
                                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                            Pref: {item.preferredName}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <small>{item.email || '—'}</small>
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {item.cellNumber || item.phoneNumber || '—'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <small>{item.mediumType || '—'}</small>
                                                </td>
                                                <td>
                                                    <small>{item.createdAt || '—'}</small>
                                                </td>
                                                <td>
                                                    <StatusBadge status={item.status} />
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleView(item.id)}
                                                            title="Edit recall pool"
                                                        >
                                                            <i className="bi bi-pencil-square"></i>
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(item)}
                                                            title="Delete recall pool"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                        <Button
                                                            variant={
                                                                expandedRowId === item.id
                                                                    ? 'outline-secondary'
                                                                    : 'outline-primary'
                                                            }
                                                            size="sm"
                                                            onClick={() => toggleRow(item.id)}
                                                            title={
                                                                expandedRowId === item.id ? 'Collapse' : 'Expand'
                                                            }
                                                            aria-expanded={expandedRowId === item.id}
                                                        >
                                                            <i
                                                                className={`bi bi-chevron-${
                                                                    expandedRowId === item.id ? 'up' : 'down'
                                                                }`}
                                                            ></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr className="p-0 border-top-0">
                                                <td colSpan={7} className="p-0 border-top-0">
                                                    <Collapse in={expandedRowId === item.id}>
                                                        <div className="p-3 bg-light border-top">
                                                            <div className="row">
                                                                <DetailField label="Pool ID" mono>
                                                                    {item.id}
                                                                </DetailField>
                                                                <DetailField label="Clinic ID" mono>
                                                                    {item.clinicId ? (
                                                                        <Link
                                                                            to={`/clinics/${item.clinicId}`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            {item.clinicId}
                                                                        </Link>
                                                                    ) : (
                                                                        '—'
                                                                    )}
                                                                </DetailField>
                                                                <DetailField label="Clinic Name">
                                                                    {item.clinicName}
                                                                </DetailField>
                                                                <DetailField label="Patient ID" mono>
                                                                    {item.patientId}
                                                                </DetailField>
                                                                <DetailField label="Recall Request ID" mono>
                                                                    {item.recallRequestId}
                                                                </DetailField>
                                                                <DetailField label="Patient">
                                                                    {patientName(item)}
                                                                </DetailField>
                                                                <DetailField label="Preferred Name">
                                                                    {item.preferredName}
                                                                </DetailField>
                                                                <DetailField label="Email">{item.email}</DetailField>
                                                                <DetailField label="Cell Number">
                                                                    {item.cellNumber}
                                                                </DetailField>
                                                                <DetailField label="Phone Number">
                                                                    {item.phoneNumber}
                                                                </DetailField>
                                                                <DetailField label="Recall Number">
                                                                    {item.recallNumber}
                                                                </DetailField>
                                                                <DetailField label="Medium">
                                                                    {item.mediumType}
                                                                </DetailField>
                                                                <DetailField label="Status">
                                                                    <StatusBadge status={item.status} />
                                                                </DetailField>
                                                                <DetailField label="Created At">
                                                                    {item.createdAt}
                                                                </DetailField>
                                                                <DetailField label="Modified At">
                                                                    {item.modifiedAt}
                                                                </DetailField>
                                                                <DetailField label="1st Recall">
                                                                    {item.firstRecallStatus ? 'Yes' : 'No'}
                                                                    {item.firstRecallDate
                                                                        ? ` · ${item.firstRecallDate}`
                                                                        : ''}
                                                                </DetailField>
                                                                <DetailField label="2nd Recall">
                                                                    {item.secondRecallStatus ? 'Yes' : 'No'}
                                                                    {item.secondRecallDate
                                                                        ? ` · ${item.secondRecallDate}`
                                                                        : ''}
                                                                </DetailField>
                                                                <DetailField label="3rd Recall">
                                                                    {item.thirdRecallStatus ? 'Yes' : 'No'}
                                                                    {item.thirdRecallDate
                                                                        ? ` · ${item.thirdRecallDate}`
                                                                        : ''}
                                                                </DetailField>
                                                                <DetailField label="4th Recall">
                                                                    {item.fourthRecallStatus ? 'Yes' : 'No'}
                                                                    {item.fourthRecallDate
                                                                        ? ` · ${item.fourthRecallDate}`
                                                                        : ''}
                                                                </DetailField>
                                                            </div>
                                                            <div className="mt-2 d-flex gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => handleView(item.id)}
                                                                >
                                                                    <i className="bi bi-pencil-square me-1"></i>
                                                                    Edit entry
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDelete(item)}
                                                                >
                                                                    <i className="bi bi-trash me-1"></i>
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </Collapse>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {error && currentItems.length > 0 && (
                <Alert variant="warning" className="mt-3 mb-0">
                    {error}
                </Alert>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '80px' }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        <span className="small">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default RecallPoolTable;
